import { BubbleMenu, EditorContent, useEditor, type Editor } from '@tiptap/react'
import { Extension, Node as TiptapNode } from '@tiptap/core'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import Highlight from '@tiptap/extension-highlight'
import TaskList from '@tiptap/extension-task-list'
import TaskItem from '@tiptap/extension-task-item'
import Table from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import Image from '@tiptap/extension-image'
import Placeholder from '@tiptap/extension-placeholder'
import CharacterCount from '@tiptap/extension-character-count'
import Mention from '@tiptap/extension-mention'
import Youtube from '@tiptap/extension-youtube'
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight'
import Suggestion, { type SuggestionOptions } from '@tiptap/suggestion'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'
import { common, createLowlight } from 'lowlight'
import { useEffect, useRef, useState } from 'react'
import { apiOrigin } from '../../lib/api.ts'
import { uploadWorkItemImage } from '../work-item/api.ts'
import './docs-editor.css'

/**
 * Área de edição do Docs refeita sobre PROSEMIRROR (via TipTap, MIT) — a
 * mesma fundação do editor do Outline (github.com/outline/outline, BSL; o
 * código deles não pode ser copiado literalmente, a fundação aberta sim).
 *
 * Comportamento espelhado do Outline: menu "/" com blocos e atalhos
 * (^⇧1..4 cabeçalhos, ^⇧7..9 listas), atalhos markdown ao digitar, bolha de
 * formatação na seleção, tabelas, lista de tarefas, imagem, VÍDEO (YouTube,
 * inclusive colando o link), menções "@", código com sintaxe destacada e
 * AUTOSAVE contínuo (nada de botão salvar).
 */

const lowlight = createLowlight(common)

// ------------------------------------------------------ popup de sugestão ---

type ItemSlash = {
  titulo: string
  atalho?: string
  icone: string
  aplicar: (editor: Editor) => void
}

/** Popup imperativo (DOM puro) usado pelo menu "/" e pelas menções "@". */
function criarPopup<T>(opts: {
  rotulo: (item: T) => string
  detalhe?: (item: T) => string
  icone?: (item: T) => string
}) {
  let el: HTMLDivElement | null = null
  let itens: T[] = []
  let indice = 0
  let escolher: (item: T) => void = () => {}

  // Marca o item ativo trocando SO a classe — nunca reconstruir o DOM aqui:
  // recriar o botao sob o ponteiro re-dispara mouseenter (loop que engole o
  // clique) e desfaz a navegacao por setas.
  const marcar = () => {
    if (!el) return
    Array.from(el.children).forEach((c, i) => c.classList.toggle('dce-menu__item--on', i === indice))
    el.children[indice]?.scrollIntoView({ block: 'nearest' })
  }

  const pintar = () => {
    if (!el) return
    el.innerHTML = ''
    itens.forEach((item, i) => {
      const b = document.createElement('button')
      b.type = 'button'
      b.className = `dce-menu__item${i === indice ? ' dce-menu__item--on' : ''}`
      if (opts.icone) {
        const ic = document.createElement('i')
        ic.className = opts.icone(item)
        b.append(ic)
      }
      const nome = document.createElement('span')
      nome.textContent = opts.rotulo(item)
      b.append(nome)
      const det = opts.detalhe?.(item)
      if (det) {
        const dt = document.createElement('span')
        dt.className = 'dce-menu__atalho'
        dt.textContent = det
        b.append(dt)
      }
      b.addEventListener('mousedown', (e) => {
        e.preventDefault()
        escolher(item)
      })
      b.addEventListener('mouseenter', () => {
        if (indice === i) return
        indice = i
        marcar()
      })
      el!.append(b)
    })
  }

  return {
    abrir(rect: DOMRect | null, novos: T[], aoEscolher: (item: T) => void) {
      itens = novos
      indice = 0
      escolher = aoEscolher
      if (!el) {
        el = document.createElement('div')
        el.className = 'dce-menu'
        document.body.append(el)
      }
      if (rect) {
        el.style.left = `${Math.min(rect.left, window.innerWidth - 300)}px`
        el.style.top = `${Math.min(rect.bottom + 6, window.innerHeight - 320)}px`
      }
      el.style.display = novos.length > 0 ? 'flex' : 'none'
      pintar()
    },
    teclar(evento: KeyboardEvent): boolean {
      if (!el || el.style.display === 'none' || itens.length === 0) return false
      if (evento.key === 'ArrowDown') {
        indice = (indice + 1) % itens.length
        marcar()
        return true
      }
      if (evento.key === 'ArrowUp') {
        indice = (indice + itens.length - 1) % itens.length
        marcar()
        return true
      }
      if (evento.key === 'Enter' || evento.key === 'Tab') {
        escolher(itens[indice]!)
        return true
      }
      if (evento.key === 'Escape') {
        this.fechar()
        return true
      }
      return false
    },
    fechar() {
      el?.remove()
      el = null
    },
  }
}

// -------------------------------------------------------------- menu "/" ---

const BLOCOS: readonly ItemSlash[] = [
  { titulo: 'Cabeçalho grande', atalho: '^⇧1', icone: 'fas fa-heading', aplicar: (e) => e.chain().focus().toggleHeading({ level: 1 }).run() },
  { titulo: 'Cabeçalho médio', atalho: '^⇧2', icone: 'fas fa-heading', aplicar: (e) => e.chain().focus().toggleHeading({ level: 2 }).run() },
  { titulo: 'Cabeçalho pequeno', atalho: '^⇧3', icone: 'fas fa-heading', aplicar: (e) => e.chain().focus().toggleHeading({ level: 3 }).run() },
  { titulo: 'Cabeçalho extra pequeno', atalho: '^⇧4', icone: 'fas fa-heading', aplicar: (e) => e.chain().focus().toggleHeading({ level: 4 }).run() },
  { titulo: 'Lista de tarefas', atalho: '^⇧7', icone: 'far fa-check-square', aplicar: (e) => e.chain().focus().toggleTaskList().run() },
  { titulo: 'Lista não ordenada', atalho: '^⇧8', icone: 'fas fa-list-ul', aplicar: (e) => e.chain().focus().toggleBulletList().run() },
  { titulo: 'Lista ordenada', atalho: '^⇧9', icone: 'fas fa-list-ol', aplicar: (e) => e.chain().focus().toggleOrderedList().run() },
  { titulo: 'Citação', atalho: '>', icone: 'fas fa-quote-right', aplicar: (e) => e.chain().focus().toggleBlockquote().run() },
  { titulo: 'Informação', icone: 'fas fa-info-circle', aplicar: (e) => e.chain().focus().setCallout('info').run() },
  { titulo: 'Aviso', icone: 'fas fa-exclamation-triangle', aplicar: (e) => e.chain().focus().setCallout('aviso').run() },
  { titulo: 'Sucesso', icone: 'fas fa-check-circle', aplicar: (e) => e.chain().focus().setCallout('ok').run() },
  { titulo: 'Bloco de código', atalho: '```', icone: 'fas fa-code', aplicar: (e) => e.chain().focus().toggleCodeBlock().run() },
  { titulo: 'Tabela', icone: 'fas fa-table', aplicar: (e) => e.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run() },
  { titulo: 'Divisor', atalho: '---', icone: 'fas fa-minus', aplicar: (e) => e.chain().focus().setHorizontalRule().run() },
  { titulo: 'Imagem', icone: 'far fa-image', aplicar: (e) => escolherImagem(e) },
  { titulo: 'Vídeo', icone: 'fas fa-video', aplicar: (e) => inserirVideo(e) },
]

/**
 * Envia a imagem para o R2 (o mesmo cofre das imagens do texto formatado) e
 * insere na POSIÇÃO informada — a do cursor ao colar/escolher, a do ponto de
 * soltura ao arrastar (SPEC-20261104-2033). Antes a imagem entrava como
 * base64 dentro do HTML do documento, ou seja, ia parar no banco.
 */
async function inserirImagem(editor: Editor, file: File, pos?: number) {
  try {
    const url = await uploadWorkItemImage(file)
    const src = url.startsWith('/') ? `${apiOrigin()}${url}` : url
    const alvo = pos ?? editor.state.selection.from
    editor.chain().focus().insertContentAt(alvo, { type: 'image', attrs: { src, alt: file.name } }).run()
  } catch {
    window.alert('Não foi possível enviar a imagem.')
  }
}

/** Imagens de um DataTransfer (área de transferência ou arrasto). */
function imagensDe(dados: DataTransfer | null): File[] {
  if (!dados) return []
  return [...dados.files].filter((f) => f.type.startsWith('image/'))
}

function escolherImagem(editor: Editor) {
  const input = document.createElement('input')
  input.type = 'file'
  input.accept = 'image/*'
  input.onchange = () => {
    const file = input.files?.[0]
    if (file) void inserirImagem(editor, file)
  }
  input.click()
}

function inserirVideo(editor: Editor) {
  const url = window.prompt('Endereço do vídeo (YouTube):')
  if (!url) return
  editor.commands.setYoutubeVideo({ src: url, width: 640, height: 360 })
}

const semAcento = (t: string) => t.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase()

/** Extensão do menu "/" — plugin Suggestion do TipTap com o popup próprio. */
const SlashMenu = Extension.create({
  name: 'menuBarra',
  addProseMirrorPlugins() {
    const popup = criarPopup<ItemSlash>({
      rotulo: (i) => i.titulo,
      detalhe: (i) => i.atalho ?? '',
      icone: (i) => i.icone,
    })
    const sugestao: Omit<SuggestionOptions<ItemSlash>, 'editor'> = {
      char: '/',
      allowSpaces: false,
      command: ({ editor, range, props }) => {
        editor.chain().focus().deleteRange(range).run()
        props.aplicar(editor as Editor)
      },
      items: ({ query }) => BLOCOS.filter((b) => semAcento(b.titulo).includes(semAcento(query))),
      render: () => ({
        onStart: (props) => popup.abrir(props.clientRect?.() ?? null, props.items, (item) => props.command(item)),
        onUpdate: (props) => popup.abrir(props.clientRect?.() ?? null, props.items, (item) => props.command(item)),
        onKeyDown: (props) => popup.teclar(props.event),
        onExit: () => popup.fechar(),
      }),
    }
    return [Suggestion({ editor: this.editor, ...sugestao })]
  },
})

// -------------------------------------------- titulos com EXTENSOR (dobra) ---

/**
 * Recolher/expandir a secao de um titulo (estilo Outline): um chevron na
 * margem dobra tudo ate o proximo titulo de nivel igual ou maior. Estado so
 * de visualizacao (decoracoes) — o conteudo salvo nao muda.
 */
const chaveDobras = new PluginKey('dobraTitulos')

const HeadingFold = Extension.create({
  name: 'dobraTitulos',
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: chaveDobras,
        state: {
          init: () => ({ dobrados: [] as number[] }),
          apply(tr, prev: { dobrados: number[] }) {
            let dobrados = prev.dobrados.map((pos) => tr.mapping.map(pos))
            const meta = tr.getMeta(chaveDobras) as { toggle?: number } | undefined
            if (meta?.toggle !== undefined) {
              const pos = meta.toggle
              dobrados = dobrados.includes(pos) ? dobrados.filter((x) => x !== pos) : [...dobrados, pos]
            }
            return { dobrados }
          },
        },
        props: {
          decorations: (state) => {
            const { dobrados } = chaveDobras.getState(state) as { dobrados: number[] }
            const decos: Decoration[] = []
            let escondendo: number | null = null
            state.doc.forEach((node, pos) => {
              if (node.type.name === 'heading') {
                const nivel = node.attrs.level as number
                if (escondendo !== null && nivel <= escondendo) escondendo = null
                if (escondendo !== null) {
                  decos.push(Decoration.node(pos, pos + node.nodeSize, { class: 'dce-oculto' }))
                  return
                }
                const dobrado = dobrados.includes(pos)
                decos.push(
                  Decoration.widget(
                    pos + 1,
                    (view) => {
                      const b = document.createElement('button')
                      b.type = 'button'
                      b.className = `dce-dobra${dobrado ? ' dce-dobra--fechada' : ''}`
                      b.title = dobrado ? 'Expandir a seção' : 'Recolher a seção'
                      b.setAttribute('aria-label', b.title)
                      b.contentEditable = 'false'
                      b.textContent = dobrado ? '▸' : '▾'
                      b.addEventListener('mousedown', (e) => {
                        e.preventDefault()
                        view.dispatch(view.state.tr.setMeta(chaveDobras, { toggle: pos }))
                      })
                      return b
                    },
                    { side: -1 },
                  ),
                )
                if (dobrado) escondendo = nivel
              } else if (escondendo !== null) {
                decos.push(Decoration.node(pos, pos + node.nodeSize, { class: 'dce-oculto' }))
              }
            })
            return DecorationSet.create(state.doc, decos)
          },
        },
      }),
    ]
  },
})

// ------------------------------------------------------------- callouts ---

/** Bloco de destaque (informação/aviso/sucesso) — mesmo HTML da v1. */
const Callout = TiptapNode.create({
  name: 'callout',
  group: 'block',
  content: 'block+',
  defining: true,
  addAttributes() {
    return {
      tipo: {
        default: 'info',
        parseHTML: (el) => /rte-callout--(\w+)/.exec(el.className)?.[1] ?? 'info',
      },
    }
  },
  parseHTML() {
    return [{ tag: 'div.rte-callout' }]
  },
  renderHTML({ node }) {
    return ['div', { class: `rte-callout rte-callout--${node.attrs.tipo}` }, 0]
  },
  addCommands() {
    return {
      setCallout:
        (tipo: string) =>
        ({ commands }) =>
          commands.wrapIn(this.name, { tipo }),
    }
  },
})

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    callout: {
      setCallout: (tipo: string) => ReturnType
    }
  }
}

// ------------------------------------------------------------ componente ---

const CORES_DESTAQUE = ['#FFF59D', '#B9F3C9', '#BBDDFF', '#FFC9C9', '#E6C9FF', '#FFD8A8'] as const

export function DocsEditor({
  conteudo,
  editavel,
  mencoes,
  aoSalvar,
  aoMedir,
}: {
  conteudo: string
  editavel: boolean
  mencoes: readonly { id: string; name: string }[]
  /** Autosave (estilo Outline): chamado ~2s depois da última digitação. */
  aoSalvar: (html: string) => void
  /** Estatísticas ao vivo (palavras). */
  aoMedir?: (palavras: number) => void
}) {
  const [cores, setCores] = useState(false)
  /** O "+" na margem da LINHA VAZIA atual (estilo Outline): abre o menu "/". */
  const [mais, setMais] = useState<{ top: number } | null>(null)
  const wrapRef = useRef<HTMLDivElement | null>(null)
  const salvarRef = useRef(aoSalvar)
  salvarRef.current = aoSalvar
  const timerRef = useRef<number | undefined>(undefined)
  const sujoRef = useRef(false)
  /** O editor para os handlers de colar/soltar (montados antes dele existir). */
  const editorRef = useRef<Editor | null>(null)

  const editor = useEditor({
    editable: editavel,
    content: conteudo,
    extensions: [
      StarterKit.configure({ codeBlock: false, heading: { levels: [1, 2, 3, 4] } }),
      Underline,
      Link.configure({ openOnClick: false, autolink: true }),
      Highlight.configure({ multicolor: true }),
      TaskList,
      TaskItem.configure({ nested: true }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      Image,
      Youtube.configure({ width: 640, height: 360, nocookie: true }),
      CodeBlockLowlight.configure({ lowlight }),
      CharacterCount,
      Placeholder.configure({ placeholder: "Digite '/' para inserir, ou comece a escrever…" }),
      Callout,
      SlashMenu,
      HeadingFold,
      Mention.configure({
        HTMLAttributes: { class: 'rte-mention' },
        renderText: ({ node }) => `@${node.attrs.label ?? node.attrs.id}`,
        suggestion: {
          char: '@',
          items: ({ query }) =>
            mencoes.filter((m) => semAcento(m.name).includes(semAcento(query))).slice(0, 8),
          command: ({ editor, range, props }) => {
            editor
              .chain()
              .focus()
              .insertContentAt(range, [
                { type: 'mention', attrs: { id: (props as unknown as { id: string }).id, label: (props as unknown as { name: string }).name } },
                { type: 'text', text: ' ' },
              ])
              .run()
          },
          render: () => {
            const popup = criarPopup<{ id: string; name: string }>({
              rotulo: (m) => m.name,
              icone: () => 'fas fa-at',
            })
            return {
              onStart: (props) => popup.abrir(props.clientRect?.() ?? null, props.items, (item) => props.command(item)),
              onUpdate: (props) => popup.abrir(props.clientRect?.() ?? null, props.items, (item) => props.command(item)),
              onKeyDown: (props) => popup.teclar(props.event),
              onExit: () => popup.fechar(),
            }
          },
        },
      }),
    ],
    // Ctrl+V de imagem entra NA LINHA DO CURSOR; arrastar entra onde soltar
    // (SPEC-20261104-2033). Colar texto segue o caminho normal do editor.
    editorProps: {
      handlePaste: (view, event) => {
        const imagens = imagensDe(event.clipboardData)
        if (imagens.length === 0 || !editorRef.current) return false
        event.preventDefault()
        const pos = view.state.selection.from
        for (const f of imagens) void inserirImagem(editorRef.current, f, pos)
        return true
      },
      handleDrop: (view, event) => {
        const imagens = imagensDe((event as DragEvent).dataTransfer)
        if (imagens.length === 0 || !editorRef.current) return false
        event.preventDefault()
        const solto = view.posAtCoords({ left: (event as DragEvent).clientX, top: (event as DragEvent).clientY })
        for (const f of imagens) void inserirImagem(editorRef.current, f, solto?.pos)
        return true
      },
    },
    onUpdate: ({ editor }) => {
      sujoRef.current = true
      aoMedir?.(editor.storage.characterCount.words())
      window.clearTimeout(timerRef.current)
      timerRef.current = window.setTimeout(() => {
        sujoRef.current = false
        salvarRef.current(editor.getHTML())
      }, 2000)
    },
  })
  editorRef.current = editor

  // troca de nivel de acesso ao vivo
  useEffect(() => {
    editor?.setEditable(editavel)
  }, [editor, editavel])

  // posiciona o "+" quando o cursor esta numa linha vazia de nivel raiz
  useEffect(() => {
    if (!editor) return
    const atualizar = () => {
      if (!editor.isEditable || !editor.isFocused) {
        setMais(null)
        return
      }
      const { selection } = editor.state
      const noAtual = selection.$from.parent
      const vazio =
        selection.empty && selection.$from.depth === 1 && noAtual.type.name === 'paragraph' && noAtual.content.size === 0
      if (!vazio) {
        setMais(null)
        return
      }
      try {
        const coords = editor.view.coordsAtPos(selection.from)
        const wrap = wrapRef.current?.getBoundingClientRect()
        if (!wrap) return
        setMais({ top: coords.top - wrap.top - 1 })
      } catch {
        setMais(null)
      }
    }
    editor.on('selectionUpdate', atualizar)
    editor.on('update', atualizar)
    editor.on('focus', atualizar)
    const esconder = () => setMais(null)
    editor.on('blur', esconder)
    return () => {
      editor.off('selectionUpdate', atualizar)
      editor.off('update', atualizar)
      editor.off('focus', atualizar)
      editor.off('blur', esconder)
    }
  }, [editor])

  // conteudo mudou POR FORA (restauração de versão): re-hidrata sem brigar
  // com a digitação — só quando não há edição pendente nem foco
  useEffect(() => {
    if (!editor || sujoRef.current || editor.isFocused) return
    if (editor.getHTML() !== conteudo) editor.commands.setContent(conteudo, false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conteudo, editor])

  // desmontou com mudanca pendente: salva na hora (nao perde nada)
  useEffect(() => {
    return () => {
      window.clearTimeout(timerRef.current)
      if (sujoRef.current && editor) salvarRef.current(editor.getHTML())
    }
  }, [editor])

  if (!editor) return null

  const B = ({ ativo, icone, rotulo, onClick }: { ativo?: boolean; icone: string; rotulo: string; onClick: () => void }) => (
    <button type="button" className={ativo ? 'dce-bolha--on' : ''} title={rotulo} aria-label={rotulo} onMouseDown={(e) => { e.preventDefault(); onClick() }}>
      <i className={icone} aria-hidden />
    </button>
  )

  return (
    <div className={`dce${editavel ? '' : ' dce--leitura'}`}>
      {editavel ? (
        <BubbleMenu editor={editor} tippyOptions={{ duration: 100, maxWidth: 480 }}>
          <div className="dce-bolha" role="toolbar" aria-label="Formatação da seleção">
            <B icone="fas fa-bold" rotulo="Negrito (Ctrl+B)" ativo={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} />
            <B icone="fas fa-italic" rotulo="Itálico (Ctrl+I)" ativo={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} />
            <B icone="fas fa-underline" rotulo="Sublinhado (Ctrl+U)" ativo={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} />
            <B icone="fas fa-strikethrough" rotulo="Tachado" ativo={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()} />
            <B icone="fas fa-code" rotulo="Código" ativo={editor.isActive('code')} onClick={() => editor.chain().focus().toggleCode().run()} />
            <B icone="fas fa-highlighter" rotulo="Destacar" ativo={editor.isActive('highlight')} onClick={() => setCores((v) => !v)} />
            <B
              icone="fas fa-link"
              rotulo="Link (Ctrl+K)"
              ativo={editor.isActive('link')}
              onClick={() => {
                const url = window.prompt('Endereço do link (https://…):', editor.getAttributes('link').href ?? '')
                if (url === null) return
                if (url === '') editor.chain().focus().unsetLink().run()
                else editor.chain().focus().setLink({ href: /^https?:\/\//.test(url) ? url : `https://${url}` }).run()
              }}
            />
            <B icone="fas fa-remove-format" rotulo="Limpar formatação" onClick={() => editor.chain().focus().unsetAllMarks().run()} />
            {cores ? (
              <span className="dce-bolha__cores">
                {CORES_DESTAQUE.map((cor) => (
                  <button
                    key={cor}
                    type="button"
                    style={{ background: cor }}
                    title="Destacar nesta cor"
                    aria-label={`Destacar na cor ${cor}`}
                    onMouseDown={(e) => {
                      e.preventDefault()
                      editor.chain().focus().setHighlight({ color: cor }).run()
                      setCores(false)
                    }}
                  />
                ))}
                <button
                  type="button"
                  className="dce-bolha__semcor"
                  title="Tirar o destaque"
                  aria-label="Tirar o destaque"
                  onMouseDown={(e) => {
                    e.preventDefault()
                    editor.chain().focus().unsetHighlight().run()
                    setCores(false)
                  }}
                >
                  <i className="fas fa-ban" aria-hidden />
                </button>
              </span>
            ) : null}
          </div>
        </BubbleMenu>
      ) : null}
      <div ref={wrapRef} className="dce-wrap">
        {mais && editavel ? (
          <button
            type="button"
            className="dce-mais"
            style={{ top: mais.top }}
            title="Inserir bloco (abre o menu /)"
            aria-label="Inserir bloco"
            onMouseDown={(e) => {
              e.preventDefault()
              editor.chain().focus().insertContent('/').run()
            }}
          >
            <i className="fas fa-plus" aria-hidden />
          </button>
        ) : null}
        <EditorContent editor={editor} />
      </div>
    </div>
  )
}
